import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { navigationLinks, companyInfo } from "../data/mock";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a2e1f]/85 backdrop-blur-xl shadow-[0_8px_30px_rgba(10,46,31,0.25)] border-b border-white/5"
          : "bg-[#0a2e1f]/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform"
              style={{
                background:
                  "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              }}
            >
              DH
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="text-white font-bold text-base tracking-tight">
                D-H Gadiola
              </div>
              <div className="text-emerald-200/80 text-[10px] uppercase tracking-[0.2em]">
                Cable Specialist
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigationLinks.map((link) => (
              <div key={link.name} className="relative group">
                <Link
                  to={link.path}
                  className="text-white/90 text-sm font-medium hover:text-emerald-300 transition-colors flex items-center gap-1 px-3 py-7"
                >
                  {link.name}
                  {link.submenu && <ChevronDown size={14} />}
                </Link>
                {link.submenu && (
                  <div className="absolute top-full left-0 hidden group-hover:block min-w-[280px] bg-white shadow-2xl rounded-xl overflow-hidden border border-slate-100 z-50">
                    {link.submenu.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.path}
                        className="block px-5 py-3 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors border-b border-slate-50 last:border-b-0"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={companyInfo.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white/90 text-sm font-medium hover:text-emerald-300 transition-colors"
            >
              <WhatsAppIcon size={16} />
              <span>{companyInfo.whatsapp}</span>
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-2.5 rounded-full text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] transition-all"
              style={{
                background:
                  "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              }}
            >
              Contact
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 py-4">
            <nav className="flex flex-col gap-1">
              {navigationLinks.map((link) => (
                <div key={link.name}>
                  <button
                    onClick={() =>
                      link.submenu
                        ? setOpenSubmenu(
                            openSubmenu === link.name ? null : link.name
                          )
                        : setMobileOpen(false)
                    }
                    className="w-full flex items-center justify-between px-3 py-3 text-white text-sm font-medium hover:bg-white/5 rounded-lg"
                  >
                    <Link to={link.path} onClick={() => setMobileOpen(false)}>
                      {link.name}
                    </Link>
                    {link.submenu && (
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${
                          openSubmenu === link.name ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>
                  {link.submenu && openSubmenu === link.name && (
                    <div className="pl-6 pb-2">
                      {link.submenu.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          onClick={() => setMobileOpen(false)}
                          className="block px-3 py-2 text-emerald-200/80 text-sm hover:text-emerald-300"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="mt-3 mx-3 inline-flex items-center justify-center px-6 py-3 rounded-full text-white text-sm font-semibold"
                style={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                }}
              >
                Contact
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
